// React Imports
import React, { useId, useState } from "react";

// 3rd Party Imports
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";

// Hooks & Helpers
import {
  generateScormPackage,
  type ScormLesson,
} from "../../../helpers/scorm/generateScormPackage";
import { type ScormEdition } from "../../../client/svcScorm";
import { useToast } from "../../../hooks/ToastContext";

const EXPORT_SCORM = "Export to SCORM";

const SCORM_EDITION_OPTIONS: { label: string; value: ScormEdition }[] = [
  { label: "SCORM 2004 3rd Edition", value: "SCORM_EDITION_2004_3RD" },
  { label: "SCORM 2004 4th Edition", value: "SCORM_EDITION_2004_4TH" },
  { label: "SCORM 1.2", value: "SCORM_EDITION_1_2" },
];

// 3rd Edition is the most broadly compatible target (e.g. Docebo rejects 4th Edition)
const DEFAULT_SCORM_EDITION: ScormEdition = "SCORM_EDITION_2004_3RD";
const SCORM_MESSAGES = {
  singleLesson:
    "Export a lesson to a SCORM package so it can be added to your internal LMS. Learners can then be assigned Security Journey training directly from your LMS.",
  multipleLessons:
    "Export all lessons in this path to a SCORM package so it can be added to your internal LMS. Learners can then be assigned Security Journey training directly from your LMS.",
  note: "Note: A single zip file will be generated, containing a zip file for each lesson within the path.",
};

export type ScormExportDialogProps = {
  visible: boolean;
  onHide: () => void;
  lessons: ScormLesson[];
  testId?: string;
};

export const ScormExportDialog: React.FC<ScormExportDialogProps> = ({
  visible,
  onHide,
  lessons,
  testId = "scorm-export-dialog",
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [edition, setEdition] = useState<ScormEdition>(DEFAULT_SCORM_EDITION);
  const editionInputId = useId();
  const isMultipleLessons = lessons.length > 1;
  const { showToast } = useToast();

  // Reset the edition on close so a prior export's choice doesn't silently carry
  // over when the dialog stays mounted (e.g. path pages, where only `visible` toggles)
  const handleHide = () => {
    setEdition(DEFAULT_SCORM_EDITION);
    onHide();
  };

  const handleGenerate = async () => {
    setIsGenerating(true);

    try {
      await generateScormPackage({ lessons, edition });
      showToast({
        severity: "success",
        summary: "Files Published!",
        detail:
          "SCORM file packed and ready! Your lessons are now securely stowed in your digital backpack, prepped for the next leg of your Security Journey\u2014happy travels on the learning trail!",
        life: 3000,
      });
    } catch (error) {
      console.error("Error exporting SCORM", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail:
          "Failed to export lessons: An unexpected error has occured.  Please refresh the page and try again",
        life: 3000,
      });
    } finally {
      setIsGenerating(false);
      handleHide();
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={EXPORT_SCORM}
      modal
      data-testid={testId}
      style={{ width: "525px" }}
      footer={
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", padding: "1rem" }}
        >
          <Button
            outlined
            label="Cancel"
            data-testid={`${testId}-cancel-button`}
            onClick={handleHide}
            disabled={isGenerating}
          />
          <Button
            autoFocus
            label="Generate"
            data-testid={`${testId}-generate-button`}
            onClick={handleGenerate}
            loading={isGenerating}
          />
        </div>
      }
    >
      <p className={`${isMultipleLessons ? "mb-4" : ""}`}>
        {isMultipleLessons ? SCORM_MESSAGES.multipleLessons : SCORM_MESSAGES.singleLesson}
      </p>

      {isMultipleLessons && <p className="text-secondary text-sm">{SCORM_MESSAGES.note}</p>}

      <div className="mt-4">
        <label htmlFor={editionInputId} className="block mb-2">
          SCORM version
        </label>
        <Dropdown
          inputId={editionInputId}
          data-testid={`${testId}-edition-dropdown`}
          value={edition}
          options={SCORM_EDITION_OPTIONS}
          onChange={(e: { value: ScormEdition }) => setEdition(e.value)}
          disabled={isGenerating}
          className="w-full"
        />
      </div>
    </Dialog>
  );
};
